import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { PettyCashService } from '../../services/pettyCash.service';
import { SegmentService } from '../../services/segment.service';
import { FundDetail, PettyCashFund } from '../../interfaces/treasury.interface';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { formatDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { ReceiptButtons, VoucherDialog } from './PettyCashParts';
import { departmentNameOf, useDepartmentOptions } from '../costcentre/DepartmentPicker'; // polish 4
import { projectLabel, useProjectOptions } from '../project/ProjectSelect'; // P1-A project

const asList = <T,>(r: unknown): T[] => (Array.isArray(r) ? r : ((r as { content?: T[] } | undefined)?.content ?? []));

/** Custodian self-service: my petty-cash funds, add vouchers, upload receipts. */
const MyPettyCash: React.FC = () => {
  const toast = useToast();
  const departmentOptions = useDepartmentOptions(AuthService.getSelectedInstitution()); // polish 4
  const projectOptions = useProjectOptions(AuthService.getSelectedInstitution()); // P1-A project
  const projectName = (id?: string | null) => { const p = id ? projectOptions.find(o => o.id === id) : undefined; return p ? projectLabel(p) : (id ? id.slice(0, 8) : '—'); };
  const [funds, setFunds] = useState<PettyCashFund[]>([]);
  const [details, setDetails] = useState<Record<string, FundDetail>>({});
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState<Account[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [voucherFor, setVoucherFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const f = await PettyCashService.myFunds();
      setFunds(f);
      const entries = await Promise.all(f.map(async x => [x.id, await PettyCashService.getFund(x.id)] as const));
      setDetails(Object.fromEntries(entries));
    } catch (e) {
      toast.showApiError(e, 'Failed to load your petty-cash funds');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  const fundKey = funds.map(f => f.id).join(',');
  useEffect(() => {
    if (funds.length === 0) return;
    (async () => {
      try {
        // Custodians have no chart-of-accounts access: the fund endpoint lists the expense accounts they may use.
        const lists = await Promise.all(funds.map(f => PettyCashService.expenseAccounts(f.id)));
        const seen = new Map<string, Account>();
        lists.flat().forEach(a => seen.set(a.id, { id: a.id, code: a.code, name: a.name, accountType: 'EXPENSE' }));
        setExpenses(Array.from(seen.values()));
        setSegments(asList<Segment>(await SegmentService.getByInstitution(0, 200)).filter(s => s.active !== false));
      } catch (e) {
        toast.showApiError(e, 'Failed to load expense accounts');
      }
    })();
  }, [fundKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box data-help-page="banking.myPettyCash.page" sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>My petty cash</Typography>
      {loading && <CircularProgress />}
      {!loading && funds.length === 0 && <Typography>You are not the custodian of any petty-cash fund.</Typography>}
      {funds.map(f => (
        <Card key={f.id} variant="outlined" sx={{ mb: 3 }} data-testid={`fund-${f.id}`}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Box>
                <Typography variant="h6">{f.name} <Chip size="small" label={humanizeEnum(f.status)} /></Typography>
                <Typography variant="body2" color="text.secondary">
                  Balance {formatMoney(f.glBalance)} of float {formatMoney(f.floatAmount)} - limit per voucher {formatMoney(f.perVoucherLimit)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Not yet replenished: {formatMoney(f.unreplenishedTotal)} ({f.unreplenishedCount} vouchers)
                </Typography>
              </Box>
              {f.status === 'ACTIVE' && (
                <Button data-help="banking.myPettyCash.addVoucher" variant="contained" startIcon={<AddIcon />} onClick={() => setVoucherFor(f.id)}>Add voucher</Button>
              )}
            </Box>
            <TableContainer data-help-section="banking.myPettyCash.vouchers" component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell data-help="banking.myPettyCash.col.number">No.</TableCell><TableCell data-help="banking.myPettyCash.col.date">Date</TableCell><TableCell data-help="banking.myPettyCash.col.expense">Expense</TableCell>{(details[f.id]?.vouchers ?? []).some(x => x.departmentId) && <TableCell data-help="banking.myPettyCash.col.department">Department</TableCell>}{(details[f.id]?.vouchers ?? []).some(x => x.projectId) && <TableCell data-help="banking.myPettyCash.col.project">Project</TableCell>}<TableCell data-help="banking.myPettyCash.col.payee">Payee</TableCell>
                    <TableCell data-help="banking.myPettyCash.col.amount" align="right">Amount</TableCell><TableCell data-help="banking.myPettyCash.col.status">Status</TableCell><TableCell data-help="banking.myPettyCash.col.receipts">Receipts</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {(details[f.id]?.vouchers ?? []).map(v => (
                    <TableRow key={v.id}>
                      <TableCell>{v.voucherNumber}</TableCell><TableCell>{formatDate(v.voucherDate)}</TableCell>
                      <TableCell>{v.expenseAccountLabel}</TableCell>{(details[f.id]?.vouchers ?? []).some(x => x.departmentId) && <TableCell data-testid="voucher-department">{departmentNameOf(departmentOptions, v.departmentId)}</TableCell>}{(details[f.id]?.vouchers ?? []).some(x => x.projectId) && <TableCell data-testid="voucher-project">{projectName(v.projectId)}</TableCell>}<TableCell>{v.payee ?? '—'}</TableCell>
                      <TableCell align="right">{formatMoney(v.amount)}</TableCell><TableCell>{humanizeEnum(v.status)}</TableCell>
                      <TableCell><ReceiptButtons voucherId={v.id} canUpload={v.status !== 'VOIDED'} /></TableCell>
                    </TableRow>
                  ))}
                  {(details[f.id]?.vouchers ?? []).length === 0 && <TableRow><TableCell colSpan={8} align="center">No vouchers yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ))}
      <VoucherDialog open={!!voucherFor} funds={funds} fundId={voucherFor ?? undefined} expenses={expenses} segments={segments}
        onClose={() => setVoucherFor(null)} onSaved={() => { setVoucherFor(null); load(); }} />
    </Box>
  );
};

export default MyPettyCash;
